import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import type { LucideIcon, LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Design system icon sizes, in px. 16 inline, 20 default, 24 headers. */
export type IconSize = number;

export interface IconProps extends Omit<LucideProps, 'size' | 'strokeWidth'> {
  icon: LucideIcon;
  size?: IconSize;
  /** Decorative icons should set aria-hidden (default). Meaningful icons need title or parent aria-label. */
  label?: string;
}

/**
 * Single icon wrapper — lucide-react only, fixed stroke, token sizes (16/20/24).
 */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { icon: Lucide, size = 20, className, label, ...rest },
  ref,
) {
  return (
    <Lucide
      ref={ref}
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      strokeWidth={1.75}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      {...rest}
    />
  );
});

export type IconButtonProps = ComponentPropsWithoutRef<'button'> & {
  icon: LucideIcon;
  label: string;
  size?: IconSize;
};

/** Icon-only control — `label` is required for accessibility. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 20, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        'focus-ring inline-flex items-center justify-center rounded-control p-2 text-ink-2',
        'hover:bg-primary-tint hover:text-primary active:scale-[0.98]',
        'disabled:pointer-events-none disabled:opacity-50',
        'transition-[transform,background-color] duration-150',
        className,
      )}
      {...rest}
    >
      <Icon icon={icon} size={size} />
    </button>
  );
});
